import React, { useState, useEffect } from "react";
import {
  Search,
  FileText,
  Trash2,
  Plus,
  X,
  Upload,
  File,
  Image,
} from "lucide-react";
import { useTheme } from "../../contexts/ThemeContext";
import { API_BASE_URL } from "../../config";
import Layout from "../Layout/Layout";
import Loader from "../utils/Loader";
import ReactPaginate from "react-paginate";
import Swal from "sweetalert2";
import TrimText from "../utils/TrimText";

const TemplatePage = () => {
  const { theme } = useTheme();
  const [text, setText] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [images, setImages] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notifications, setNotifications] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasSalutation, setHasSalutation] = useState(false);
  const templatesPerPage = 30;

  const showNotification = (message, type = "success") => {
    const id = Date.now();
    const notification = { id, message, type };
    setNotifications((prev) => [...prev, notification]);
    setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    }, 4000);
  };

  useEffect(() => {
    const fetchTemplates = async () => {
      setLoading(true);
      const token = window.__appSession.getToken();
      try {
        const response = await fetch(`${API_BASE_URL}/api/templates`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await response.json();
        setTemplates(data);
      } catch (error) {
        showNotification("Failed to fetch templates.", "error");
      } finally {
        setLoading(false);
      }
    };
    fetchTemplates();
  }, []);

  const handleTemplateSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validate: If salutation is enabled, ensure {name} placeholder exists
    if (hasSalutation && !text.includes("{name}")) {
      showNotification(
        "Please include {name} placeholder in your template when salutation is enabled.",
        "error",
      );
      setLoading(false);
      return;
    }

    const token = window.__appSession.getToken();
    const formData = new FormData();
    images.forEach((image) => formData.append("files", image));

    let fileUrls = [];
    try {
      if (images.length > 0) {
        const uploadResponse = await fetch(
          `${API_BASE_URL}/api/whatsapp/upload`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: formData,
          },
        );
        const uploadData = await uploadResponse.json();
        fileUrls = uploadData.filePathUrls;
      }

      const templateFormData = new FormData();
      templateFormData.append("text", text);
      templateFormData.append("templateName", templateName);
      templateFormData.append("hasSalutation", hasSalutation);
      fileUrls.forEach((url) => templateFormData.append("imageUrls", url));

      const response = await fetch(`${API_BASE_URL}/api/templates`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: templateFormData,
      });

      const newTemplate = await response.json();
      setTemplates([...templates, newTemplate]);
      showNotification("Template created successfully!", "success");
      setText("");
      setTemplateName("");
      setImages([]);
      setHasSalutation(false);
      setIsModalOpen(false);
    } catch (error) {
      showNotification("Failed to create template.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    const token = window.__appSession.getToken();

    // Show SweetAlert2 confirmation dialog
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        await fetch(`${API_BASE_URL}/api/templates/${templateId}`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setTemplates(
          templates.filter((template) => template.templateId !== templateId),
        );
        showNotification("Template deleted successfully!", "success");
        Swal.fire("Deleted!", "Your template has been deleted.", "success");
      } catch (error) {
        showNotification("Failed to delete template.", "error");
        Swal.fire("Error!", "Failed to delete the template.", "error");
      }
    }
  };

  const filteredTemplates = templates.filter(
    (template) =>
      template.templateName &&
      template.templateName.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const MAX_SIZE = 25 * 1024 * 1024;

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter((file) => {
      if (file.size > MAX_SIZE) {
        setError("File size exceeds the 25MB limit.");
        return false;
      }
      return true;
    });

    if (validFiles.length > 0) {
      setImages(validFiles);
      setError("");
    }
  };

  const generatePreview = (file) => {
    const fileType = file.type.split("/")[0];
    const previewUrl = URL.createObjectURL(file);

    if (fileType === "image") {
      return (
        <img
          src={previewUrl}
          alt={file.name}
          className="w-16 h-16 object-cover rounded-lg"
        />
      );
    }
    if (fileType === "video") {
      return (
        <video
          src={previewUrl}
          controls
          className="w-16 h-16 object-cover rounded-lg"
        >
          Your browser does not support the video tag.
        </video>
      );
    }
    if (fileType === "application" && file.name.endsWith(".pdf")) {
      return (
        <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-lg">
          <File className="text-gray-500 h-6 w-6" />
        </div>
      );
    }
    return null;
  };

  // Pagination logic
  const pageCount = Math.ceil(filteredTemplates.length / templatesPerPage);
  const offset = currentPage * templatesPerPage;
  const currentTemplates = filteredTemplates.slice(
    offset,
    offset + templatesPerPage,
  );

  const handlePageChange = ({ selected }) => {
    setCurrentPage(selected);
  };

  return (
    <>
      <Layout>
        <div>
          {/* Notifications */}
          <div className="fixed top-4 right-4 z-50 space-y-2">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className={`rounded-xl border px-3 py-2 text-xs font-medium shadow-lg backdrop-blur ${
                  notification.type === "success"
                    ? "border-emerald-200 bg-emerald-500/95 text-white"
                    : notification.type === "error"
                      ? "border-rose-200 bg-rose-500/95 text-white"
                      : "border-indigo-200 bg-indigo-500/95 text-white"
                }`}
              >
                {notification.message}
              </div>
            ))}
          </div>

          {loading && <Loader theme={theme} text="Loading templates..." />}

          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-[max-content_1fr] gap-3 mb-3">
              {/* Header */}
              <div
                className={`glass-surface-strong ${theme.contentBg} ${theme.contentBorder} border rounded-2xl px-3 py-2`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center space-x-2">
                    <div className="rounded-xl bg-gradient-to-br from-indigo-600 to-violet-600 p-2 shadow-[0_14px_30px_-22px_rgba(79,70,229,0.9)]">
                      <FileText className="h-4 w-4 text-white" />
                    </div>
                    <div>
                      <h1 className="text-base font-semibold text-slate-900">
                        Message Templates
                      </h1>
                      <p className="text-xs text-slate-500">
                        Create and manage your message templates
                      </p>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="text-center">
                    <div className="flex items-center space-x-1">
                      <FileText className="h-3 w-3 text-indigo-500" />
                      <span className="text-xs font-medium text-slate-900">
                        {templates.length}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500">Templates</p>
                  </div>
                </div>
              </div>

              {/* Controls */}
              <div
                className={`glass-surface ${theme.contentBg} ${theme.contentBorder} border rounded-2xl flex w-full items-center px-3 py-2`}
              >
                <div className="flex flex-col w-full sm:flex-row gap-3 items-start sm:items-center justify-between">
                  {/* Search */}
                  <div className="relative flex-1 w-full">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-3 w-3 text-slate-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setCurrentPage(0); // Reset to first page on search
                      }}
                      placeholder="Search templates..."
                      className={`w-full rounded-xl border border-indigo-200 bg-white/85 pl-9 pr-3 py-2 text-xs text-slate-700 focus:outline-none focus:ring-2 ${theme.inputFocusRing}`}
                    />
                  </div>

                  {/* Action Button */}
                  <button
                    onClick={() => setIsModalOpen(true)}
                    className="flex items-center space-x-2 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 px-3 py-2 text-xs font-medium text-white transition hover:opacity-95"
                  >
                    <Plus className="h-3 w-3" />
                    <span>New Template</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Templates Table */}
            <div
              className={`glass-surface ${theme.contentBg} ${theme.contentBorder} border rounded-2xl overflow-hidden`}
            >
              <div className="p-3 border-b border-indigo-200">
                <h3 className="text-sm font-semibold text-slate-900 flex items-center">
                  <FileText className="h-4 w-4 mr-2" />
                  Templates ({filteredTemplates.length})
                </h3>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-indigo-100/90 via-violet-100/90 to-indigo-100/90 top-0 sticky z-10">
                    <tr>
                      <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-700">
                        #
                      </th>
                      <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-700">
                        Template Name
                      </th>
                      <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-700">
                        Content
                      </th>
                      <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-700">
                        Files
                      </th>
                      <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-700">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-indigo-100/80">
                    {currentTemplates.length === 0 ? (
                      <tr>
                        <td colSpan={10} className="px-3 py-6 text-center">
                          <FileText className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                          <p className="text-xs text-slate-500">
                            No templates found
                          </p>
                        </td>
                      </tr>
                    ) : (
                      currentTemplates.map((template, idx) => (
                        <tr
                          key={template.templateId}
                          className="text-xs transition-colors hover:bg-indigo-50/55"
                        >
                          <td className="px-3 py-1">{offset + idx + 1}</td>
                          <td className="px-3 py-1">
                            <div className="flex items-center">
                              <span className="inline-flex items-center rounded-full border border-indigo-200 bg-indigo-100 px-2 py-1 text-xs font-medium text-indigo-700">
                                {template.templateName}
                              </span>
                            </div>
                          </td>
                          <td className="px-3 py-1 text-xs text-slate-700 max-w-xs">
                            <div title={template.text}>
                              {template.hasSalutation && (
                                <span className="mr-2 inline-flex items-center rounded border border-violet-200 bg-violet-100 px-2 py-0.5 text-xs font-medium text-violet-700">
                                  Salutation
                                </span>
                              )}
                              {<TrimText text={template.text} maxLen={60} />}
                            </div>
                          </td>
                          <td className="px-3 py-1">
                            {template.imageUrls &&
                            template.imageUrls.length > 0 ? (
                              <div className="flex space-x-1">
                                {template.imageUrls
                                  .slice(0, 3)
                                  .map((mediaUrl, index) => {
                                    const isVideo =
                                      mediaUrl.endsWith(".mp4") ||
                                      mediaUrl.endsWith(".mov");
                                    const isRawUpload =
                                      mediaUrl.includes("/raw/upload");

                                    return (
                                      <div
                                        key={index}
                                        className="h-8 w-8 overflow-hidden rounded border border-indigo-200"
                                      >
                                        {isRawUpload ? (
                                          <div className="flex h-full w-full items-center justify-center bg-indigo-50">
                                            <File className="h-3 w-3 text-slate-400" />
                                          </div>
                                        ) : isVideo ? (
                                          <video
                                            src={mediaUrl}
                                            className="object-cover w-full h-full"
                                          />
                                        ) : (
                                          <img
                                            src={mediaUrl}
                                            alt={`Template ${index}`}
                                            className="object-cover w-full h-full"
                                          />
                                        )}
                                      </div>
                                    );
                                  })}
                                {template.imageUrls.length > 3 && (
                                  <div className="flex h-8 w-8 items-center justify-center rounded border border-indigo-200 bg-indigo-50">
                                    <span className="text-xs text-slate-500">
                                      +{template.imageUrls.length - 3}
                                    </span>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="text-xs text-slate-400">
                                No files
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-1">
                            <button
                              onClick={() =>
                                handleDeleteTemplate(template.templateId)
                              }
                              className="rounded-md p-1 text-rose-500 transition-colors hover:bg-rose-50 hover:text-rose-700"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Table Footer */}
              <div className="border-t border-indigo-200 bg-indigo-50/40 px-3 py-2">
                <div className="flex items-center justify-between">
                  <div className="text-xs text-slate-700">
                    Showing {currentTemplates.length} of{" "}
                    {filteredTemplates.length} templates
                  </div>
                  <div className="text-xs text-slate-500">
                    Total: {templates.length} templates
                  </div>
                  {/* Pagination */}
                  {pageCount > 1 && (
                    <div>
                      <ReactPaginate
                        previousLabel={"Prev"}
                        nextLabel={"Next"}
                        breakLabel={"..."}
                        pageCount={pageCount}
                        marginPagesDisplayed={2}
                        pageRangeDisplayed={5}
                        onPageChange={handlePageChange}
                        containerClassName={
                          "flex items-center justify-center space-x-2 text-xs"
                        }
                        pageClassName={"rounded-lg border border-indigo-200 bg-white px-3 py-1 hover:bg-indigo-50"}
                        pageLinkClassName={"text-slate-700"}
                        previousClassName={
                          "rounded-lg border border-indigo-200 bg-white px-3 py-1 hover:bg-indigo-50"
                        }
                        previousLinkClassName={"text-slate-700"}
                        nextClassName={"rounded-lg border border-indigo-200 bg-white px-3 py-1 hover:bg-indigo-50"}
                        nextLinkClassName={"text-slate-700"}
                        breakClassName={"px-3 py-1"}
                        breakLinkClassName={"text-slate-700"}
                        activeClassName={"!border-indigo-600 !bg-indigo-600 text-white"}
                        disabledClassName={"opacity-50 cursor-not-allowed"}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Layout>
      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 p-4">
          {loading && <Loader theme={theme} text="Creating templates..." />}

          <div
            className={`glass-surface-strong ${theme.contentBg} w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl border border-indigo-200/70 shadow-xl`}
          >
            <div className="flex items-center justify-between border-b border-indigo-200 px-4 py-3">
              <h3 className="text-sm font-semibold text-slate-900 flex items-center">
                <FileText className="h-4 w-4 mr-2" />
                Create New Template
              </h3>
              <button
                onClick={() => setIsModalOpen(false)}
                className="rounded-lg border border-indigo-200 p-1 text-slate-500 transition-colors hover:bg-indigo-50 hover:text-slate-700"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <form onSubmit={handleTemplateSubmit} className="p-4 space-y-3">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Template Name
                </label>
                <input
                  type="text"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="Enter template name"
                  className={`w-full rounded-xl border border-indigo-200 bg-white/90 px-3 py-2 text-xs text-slate-700 focus:outline-none focus:ring-2 ${theme.inputFocusRing}`}
                  required
                />
              </div>

              <div>
                <label className="flex items-center space-x-2 mb-2">
                  <input
                    type="checkbox"
                    checked={hasSalutation}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setHasSalutation(checked);
                      if (checked) {
                        // Auto-populate with salutation only if no {name} placeholder exists in the text
                        // This allows users to customize the salutation (e.g., "Hi {name}", "Hello {name}")
                        if (!text.includes("{name}")) {
                          setText(`Dear {name}\n\n${text.trim()}`);
                        }
                      } else {
                        // Remove any salutation pattern with {name} if unchecked
                        // Match patterns like "Dear {name}", "Hi {name}", etc. at the start
                        const cleanedText = text
                          .replace(/^[^\n]*(?:\{name\})[^\n]*\s*\n\s*/i, "")
                          .trim();
                        setText(cleanedText);
                      }
                    }}
                    className={`rounded border-indigo-300 text-indigo-600 focus:ring-2 ${theme.inputFocusRing}`}
                  />
                  <span className="text-xs font-medium text-slate-700">
                    Enable Salutation (Auto-add &quot;Dear {"{"}
                    {"name"}
                    {"}"}&quot;)
                  </span>
                </label>
                <textarea
                  value={text}
                  onChange={(e) => {
                    setText(e.target.value);
                  }}
                  placeholder={
                    hasSalutation
                      ? "Enter salutation with {name} placeholder, e.g., Hi {name} or Dear {name}..."
                      : "Enter template content..."
                  }
                  rows={4}
                  className={`w-full resize-none rounded-xl border border-indigo-200 bg-white/90 px-3 py-2 text-xs text-slate-700 focus:outline-none focus:ring-2 ${theme.inputFocusRing}`}
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Files (Images, PDFs - Max 25MB)
                </label>
                <div className="rounded-xl border-2 border-dashed border-indigo-200 bg-white/75 p-4 text-center transition-colors hover:border-indigo-300">
                  <input
                    type="file"
                    multiple
                    accept="image/*,application/pdf"
                    onChange={handleFileChange}
                    className="hidden"
                    id="file-upload"
                  />
                  <label htmlFor="file-upload" className="cursor-pointer">
                    <Upload className="mx-auto mb-2 h-6 w-6 text-slate-400" />
                    <p className="text-xs text-slate-600">
                      Click to select files
                    </p>
                  </label>
                </div>
                {error && <p className="text-red-600 text-xs mt-1">{error}</p>}
                {images.length > 0 && (
                  <div className="mt-3 flex gap-2 flex-wrap">
                    {images.map((file, index) => (
                      <div key={index} className="relative group">
                        {generatePreview(file)}
                        <p className="text-center text-xs mt-1  max-w-[64px]">
                          {file.name}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex space-x-2 pt-2">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="flex-1 rounded-xl border border-indigo-200 bg-white px-4 py-2 text-xs font-medium text-slate-700 transition-colors hover:bg-indigo-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex flex-1 items-center justify-center space-x-2 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 px-4 py-2 text-xs font-medium text-white transition-opacity hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <FileText className="h-3 w-3" />
                  <span>Create Template</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default TemplatePage;

// import React, { useState, useEffect } from "react";
// import {
//   Search,
//   FileText,
//   Trash2,
//   Plus,
//   X,
//   Upload,
//   File,
//   Image,
// } from "lucide-react";
// import { useTheme } from "../../contexts/ThemeContext";
// import { API_BASE_URL } from "../../config";
// import Layout from "../Layout/Layout";
// import Loader from "../utils/Loader";

// const TemplatePage = () => {
//   const { theme } = useTheme();
//   const [text, setText] = useState("");
//   const [templateName, setTemplateName] = useState("");
//   const [images, setImages] = useState([]);
//   const [templates, setTemplates] = useState([]);
//   const [searchQuery, setSearchQuery] = useState("");
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState("");
//   const [notifications, setNotifications] = useState([]);
//   const [isModalOpen, setIsModalOpen] = useState(false);

//   const showNotification = (message, type = "success") => {
//     const id = Date.now();
//     const notification = { id, message, type };
//     setNotifications((prev) => [...prev, notification]);
//     setTimeout(() => {
//       setNotifications((prev) => prev.filter((n) => n.id !== id));
//     }, 4000);
//   };

//   useEffect(() => {
//     const fetchTemplates = async () => {
//       setLoading(true);
//       const token = window.__appSession.getToken();
//       try {
//         const response = await fetch(`${API_BASE_URL}/api/templates`, {
//           headers: {
//             Authorization: `Bearer ${token}`,
//           },
//         });
//         const data = await response.json();
//         setTemplates(data);
//       } catch (error) {
//         showNotification("Failed to fetch templates.", "error");
//       } finally {
//         setLoading(false);
//       }
//     };
//     fetchTemplates();
//   }, []);

//   const handleTemplateSubmit = async (e) => {
//     e.preventDefault();
//     setLoading(true);

//     const token = window.__appSession.getToken();
//     const formData = new FormData();
//     images.forEach((image) => formData.append("files", image));

//     let fileUrls = [];
//     try {
//       if (images.length > 0) {
//         const uploadResponse = await fetch(
//           `${API_BASE_URL}/api/whatsapp/upload`,
//           {
//             method: "POST",
//             headers: {
//               Authorization: `Bearer ${token}`,
//             },
//             body: formData,
//           }
//         );
//         const uploadData = await uploadResponse.json();
//         fileUrls = uploadData.filePathUrls;
//       }

//       const templateFormData = new FormData();
//       templateFormData.append("text", text);
//       templateFormData.append("templateName", templateName);
//       fileUrls.forEach((url) => templateFormData.append("imageUrls", url));

//       const response = await fetch(`${API_BASE_URL}/api/templates`, {
//         method: "POST",
//         headers: {
//           Authorization: `Bearer ${token}`,
//         },
//         body: templateFormData,
//       });

//       const newTemplate = await response.json();
//       setTemplates([...templates, newTemplate]);
//       showNotification("Template created successfully!", "success");
//       setText("");
//       setTemplateName("");
//       setImages([]);
//       setIsModalOpen(false);
//     } catch (error) {
//       showNotification("Failed to create template.", "error");
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleDeleteTemplate = async (templateId) => {
//     const token = window.__appSession.getToken();
//     try {
//       await fetch(`${API_BASE_URL}/api/templates/${templateId}`, {
//         method: "DELETE",
//         headers: {
//           Authorization: `Bearer ${token}`,
//         },
//       });
//       setTemplates(
//         templates.filter((template) => template.templateId !== templateId)
//       );
//       showNotification("Template deleted successfully!", "success");
//     } catch (error) {
//       showNotification("Failed to delete template.", "error");
//     }
//   };

//   const filteredTemplates = templates.filter((template) =>
//     template.templateName.toLowerCase().includes(searchQuery.toLowerCase())
//   );

//   const MAX_SIZE = 25 * 1024 * 1024;

//   const handleFileChange = (e) => {
//     const files = Array.from(e.target.files);
//     const validFiles = files.filter((file) => {
//       if (file.size > MAX_SIZE) {
//         setError("File size exceeds the 25MB limit.");
//         return false;
//       }
//       return true;
//     });

//     if (validFiles.length > 0) {
//       setImages(validFiles);
//       setError("");
//     }
//   };

//   const generatePreview = (file) => {
//     const fileType = file.type.split("/")[0];
//     const previewUrl = URL.createObjectURL(file);

//     if (fileType === "image") {
//       return (
//         <img
//           src={previewUrl}
//           alt={file.name}
//           className="w-16 h-16 object-cover rounded-lg"
//         />
//       );
//     }
//     if (fileType === "video") {
//       return (
//         <video
//           src={previewUrl}
//           controls
//           className="w-16 h-16 object-cover rounded-lg"
//         >
//           Your browser does not support the video tag.
//         </video>
//       );
//     }
//     if (fileType === "application" && file.name.endsWith(".pdf")) {
//       return (
//         <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-lg">
//           <File className="text-gray-500 h-6 w-6" />
//         </div>
//       );
//     }
//     return null;
//   };

//   return (
//     <Layout>
//       <div className={`min-h-screen ${theme.mainBg} py-3 px-3`}>
//         {/* Notifications */}
//         <div className="fixed top-4 right-4 z-50 space-y-2">
//           {notifications.map((notification) => (
//             <div
//               key={notification.id}
//               className={`px-3 py-2 rounded-lg shadow-lg text-xs font-medium transition-all duration-300 ${
//                 notification.type === "success"
//                   ? "bg-green-500 text-white"
//                   : notification.type === "error"
//                   ? "bg-red-500 text-white"
//                   : "bg-primary-500 text-white"
//               }`}
//             >
//               {notification.message}
//             </div>
//           ))}
//         </div>

//         {/* Loading Overlay */}
//         {/* {loading && (
//           <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//             <div className={`${theme.contentBg} p-4 rounded-lg shadow-xl`}>
//               <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-current mx-auto"></div>
//               <p className="mt-2 text-xs text-gray-600">Loading...</p>
//             </div>
//           </div>
//         )} */}

//         {loading && <Loader theme={theme} text="Loading templates..." />}

//         {/* Modal */}
//         {isModalOpen && (
//           <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//             <div
//               className={`${theme.contentBg} rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto`}
//             >
//               <div className="flex items-center justify-between p-4 border-b border-gray-200">
//                 <h3 className="text-sm font-semibold text-gray-900 flex items-center">
//                   <FileText className="h-4 w-4 mr-2" />
//                   Create New Template
//                 </h3>
//                 <button
//                   onClick={() => setIsModalOpen(false)}
//                   className="text-gray-400 hover:text-gray-600 transition-colors"
//                 >
//                   <X className="h-4 w-4" />
//                 </button>
//               </div>

//               <form onSubmit={handleTemplateSubmit} className="p-4 space-y-3">
//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-1">
//                     Template Name
//                   </label>
//                   <input
//                     type="text"
//                     value={templateName}
//                     onChange={(e) => setTemplateName(e.target.value)}
//                     placeholder="Enter template name"
//                     className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50 focus:ring-current"
//                     required
//                   />
//                 </div>

//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-1">
//                     Template Text
//                   </label>
//                   <textarea
//                     value={text}
//                     onChange={(e) => setText(e.target.value)}
//                     placeholder="Enter template content..."
//                     rows={4}
//                     className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50 focus:ring-current resize-none"
//                     required
//                   />
//                 </div>

//                 <div>
//                   <label className="block text-xs font-medium text-gray-700 mb-1">
//                     Files (Images, PDFs - Max 25MB)
//                   </label>
//                   <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-gray-400 transition-colors">
//                     <input
//                       type="file"
//                       multiple
//                       accept="image/*,application/pdf"
//                       onChange={handleFileChange}
//                       className="hidden"
//                       id="file-upload"
//                     />
//                     <label htmlFor="file-upload" className="cursor-pointer">
//                       <Upload className="h-6 w-6 text-gray-400 mx-auto mb-2" />
//                       <p className="text-xs text-gray-600">
//                         Click to select files
//                       </p>
//                     </label>
//                   </div>
//                   {error && (
//                     <p className="text-red-600 text-xs mt-1">{error}</p>
//                   )}
//                   {images.length > 0 && (
//                     <div className="mt-3 flex gap-2 flex-wrap">
//                       {images.map((file, index) => (
//                         <div key={index} className="relative group">
//                           {generatePreview(file)}
//                           <p className="text-center text-xs mt-1  max-w-[64px]">
//                             {file.name}
//                           </p>
//                         </div>
//                       ))}
//                     </div>
//                   )}
//                 </div>

//                 <div className="flex space-x-2 pt-2">
//                   <button
//                     type="button"
//                     onClick={() => setIsModalOpen(false)}
//                     className="flex-1 px-4 py-2 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
//                   >
//                     Cancel
//                   </button>
//                   <button
//                     type="submit"
//                     disabled={loading}
//                     className={`flex-1 ${theme.accentColor} text-white px-4 py-2 text-xs font-medium rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2`}
//                   >
//                     <FileText className="h-3 w-3" />
//                     <span>Create Template</span>
//                   </button>
//                 </div>
//               </form>
//             </div>
//           </div>
//         )}

//         <div className="max-w-6xl mx-auto">
//           {/* Header */}
//           <div
//             className={`${theme.contentBg} ${theme.contentBorder} border rounded-lg shadow-sm p-3 mb-3`}
//           >
//             <div className="flex items-center justify-between">
//               <div className="flex items-center space-x-2">
//                 <div
//                   className={`p-2 ${theme.accentColor} bg-opacity-10 rounded-lg`}
//                 >
//                   <FileText className={`h-4 w-4 ${theme.iconColor}`} />
//                 </div>
//                 <div>
//                   <h1 className="text-base font-semibold text-gray-900">
//                     Message Templates
//                   </h1>
//                   <p className="text-xs text-gray-500">
//                     Create and manage your message templates
//                   </p>
//                 </div>
//               </div>

//               {/* Stats */}
//               <div className="text-center">
//                 <div className="flex items-center space-x-1">
//                   <FileText className="h-3 w-3 text-primary-500" />
//                   <span className="text-xs font-medium text-gray-900">
//                     {templates.length}
//                   </span>
//                 </div>
//                 <p className="text-xs text-gray-500">Templates</p>
//               </div>
//             </div>
//           </div>

//           {/* Controls */}
//           <div
//             className={`${theme.contentBg} ${theme.contentBorder} border rounded-lg shadow-sm p-3 mb-3`}
//           >
//             <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
//               {/* Search */}
//               <div className="relative flex-1 max-w-md">
//                 <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400" />
//                 <input
//                   type="text"
//                   value={searchQuery}
//                   onChange={(e) => setSearchQuery(e.target.value)}
//                   placeholder="Search templates..."
//                   className="w-full pl-9 pr-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50 focus:ring-current"
//                 />
//               </div>

//               {/* Action Button */}
//               <button
//                 onClick={() => setIsModalOpen(true)}
//                 className={`flex items-center space-x-2 px-3 py-2 text-xs font-medium rounded-lg transition-colors ${theme.accentColor} text-white hover:opacity-90`}
//               >
//                 <Plus className="h-3 w-3" />
//                 <span>New Template</span>
//               </button>
//             </div>
//           </div>

//           {/* Templates Table */}
//           <div
//             className={`${theme.contentBg} ${theme.contentBorder} border rounded-lg shadow-sm overflow-hidden`}
//           >
//             <div className="p-3 border-b border-gray-200">
//               <h3 className="text-sm font-semibold text-gray-900 flex items-center">
//                 <FileText className="h-4 w-4 mr-2" />
//                 Templates ({filteredTemplates.length})
//               </h3>
//             </div>

//             <div className="overflow-x-auto">
//               <table className="w-full">
//                 <thead className="bg-gray-50 border-b border-gray-200">
//                   <tr>
//                     <th className="px-3 py-2 text-left text-xs font-medium text-gray-800">
//                       Template Name
//                     </th>
//                     <th className="px-3 py-2 text-left text-xs font-medium text-gray-800">
//                       Content
//                     </th>
//                     <th className="px-3 py-2 text-left text-xs font-medium text-gray-800">
//                       Files
//                     </th>
//                     <th className="px-3 py-2 text-left text-xs font-medium text-gray-800">
//                       Actions
//                     </th>
//                   </tr>
//                 </thead>
//                 <tbody className="divide-y divide-gray-200">
//                   {filteredTemplates.length === 0 ? (
//                     <tr>
//                       <td colSpan={4} className="px-3 py-6 text-center">
//                         <FileText className="h-8 w-8 text-gray-400 mx-auto mb-2" />
//                         <p className="text-xs text-gray-500">
//                           No templates found
//                         </p>
//                       </td>
//                     </tr>
//                   ) : (
//                     filteredTemplates.map((template) => (
//                       <tr
//                         key={template.templateId}
//                         className="hover:bg-gray-50 transition-colors"
//                       >
//                         <td className="px-3 py-2">
//                           <div className="flex items-center">
//                             <span
//                               className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${theme.accentColor} bg-opacity-10 text-current`}
//                             >
//                               {template.templateName}
//                             </span>
//                           </div>
//                         </td>
//                         <td className="px-3 py-2 text-xs text-gray-700 max-w-xs">
//                           <div className="" title={template.text}>
//                             {template.text}
//                           </div>
//                         </td>
//                         <td className="px-3 py-2">
//                           {template.imageUrls &&
//                           template.imageUrls.length > 0 ? (
//                             <div className="flex space-x-1">
//                               {template.imageUrls
//                                 .slice(0, 3)
//                                 .map((mediaUrl, index) => {
//                                   const isVideo =
//                                     mediaUrl.endsWith(".mp4") ||
//                                     mediaUrl.endsWith(".mov");
//                                   const isRawUpload =
//                                     mediaUrl.includes("/raw/upload");

//                                   return (
//                                     <div
//                                       key={index}
//                                       className="w-8 h-8 overflow-hidden rounded border border-gray-200"
//                                     >
//                                       {isRawUpload ? (
//                                         <div className="flex items-center justify-center w-full h-full bg-gray-100">
//                                           <File className="text-gray-400 h-3 w-3" />
//                                         </div>
//                                       ) : isVideo ? (
//                                         <video
//                                           src={mediaUrl}
//                                           className="object-cover w-full h-full"
//                                         />
//                                       ) : (
//                                         <img
//                                           src={mediaUrl}
//                                           alt={`Template ${index}`}
//                                           className="object-cover w-full h-full"
//                                         />
//                                       )}
//                                     </div>
//                                   );
//                                 })}
//                               {template.imageUrls.length > 3 && (
//                                 <div className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded border border-gray-200">
//                                   <span className="text-xs text-gray-500">
//                                     +{template.imageUrls.length - 3}
//                                   </span>
//                                 </div>
//                               )}
//                             </div>
//                           ) : (
//                             <span className="text-xs text-gray-400">
//                               No files
//                             </span>
//                           )}
//                         </td>
//                         <td className="px-3 py-2">
//                           <button
//                             onClick={() =>
//                               handleDeleteTemplate(template.templateId)
//                             }
//                             className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
//                           >
//                             <Trash2 className="h-3 w-3" />
//                           </button>
//                         </td>
//                       </tr>
//                     ))
//                   )}
//                 </tbody>
//               </table>
//             </div>

//             {/* Table Footer */}
//             <div className="bg-gray-50 px-3 py-2 border-t border-gray-200">
//               <div className="flex items-center justify-between">
//                 <div className="text-xs text-gray-700">
//                   Showing {filteredTemplates.length} of {templates.length}{" "}
//                   templates
//                 </div>
//                 <div className="text-xs text-gray-500">
//                   Total: {templates.length} templates
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//     </Layout>
//   );
// };

// export default TemplatePage;
